import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Search, Filter, Download, RefreshCw, ChevronDown, ChevronUp,
  Truck, ShoppingBag, Users, MapPin, Phone, Clock, Euro,
  CheckCircle, XCircle, AlertCircle, Eye, MoreHorizontal,
  Calendar, ArrowUpDown, ArrowUp, ArrowDown, X, Printer,
  CreditCard, Banknote, Smartphone, Store, ExternalLink,
  Edit, Save, Plus, Minus, Trash2, Package, Receipt,
  Zap, Timer, UserCheck, ChefHat, Car, Home
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Bestelltypen
const ORDER_TYPES = {
  delivery: { label: 'Lieferung', icon: Truck, color: 'blue' },
  pickup: { label: 'Abholung', icon: ShoppingBag, color: 'green' },
  table: { label: 'Tisch', icon: Users, color: 'purple' },
  portal: { label: 'Portal', icon: Smartphone, color: 'orange' }
};

// Status mit Farben und Icons
const ORDER_STATUS = {
  new: { label: 'Neu', color: 'bg-yellow-500', textColor: 'text-yellow-500', icon: AlertCircle, priority: 1 },
  confirmed: { label: 'Bestätigt', color: 'bg-blue-500', textColor: 'text-blue-500', icon: CheckCircle, priority: 2 },
  preparing: { label: 'Zubereitung', color: 'bg-purple-500', textColor: 'text-purple-500', icon: ChefHat, priority: 3 },
  ready: { label: 'Fertig', color: 'bg-emerald-500', textColor: 'text-emerald-500', icon: CheckCircle, priority: 4 },
  on_delivery: { label: 'Unterwegs', color: 'bg-orange-500', textColor: 'text-orange-500', icon: Car, priority: 5 },
  dispatched: { label: 'Unterwegs', color: 'bg-orange-500', textColor: 'text-orange-500', icon: Car, priority: 5 },
  completed: { label: 'Abgeschlossen', color: 'bg-green-600', textColor: 'text-green-600', icon: CheckCircle, priority: 99 },
  delivered: { label: 'Zugestellt', color: 'bg-green-600', textColor: 'text-green-600', icon: Home, priority: 99 },
  cancelled: { label: 'Storniert', color: 'bg-red-500', textColor: 'text-red-500', icon: XCircle, priority: 100 }
};

// Zahlungsarten
const PAYMENT_METHODS = {
  cash: { label: 'Bar', icon: Banknote },
  card: { label: 'Karte', icon: CreditCard },
  online: { label: 'Online', icon: Smartphone },
  portal: { label: 'Portal', icon: Store }
};

// Portale/Broker
const PORTALS = {
  lieferando: { label: 'Lieferando', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  wolt: { label: 'Wolt', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  uber_eats: { label: 'UberEats', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  own: { label: 'Eigen', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  pos: { label: 'POS', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' }
};

export default function Bestelluebersicht() {
  // Main State
  const [activeTab, setActiveTab] = useState('live');
  const [orders, setOrders] = useState([]);
  const [liveOrders, setLiveOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [customDate, setCustomDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Sorting
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Detail/Edit Dialog
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedOrder, setEditedOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Produkte für Artikel hinzufügen
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductPicker, setShowProductPicker] = useState(false);
  
  // Fahrer
  const [drivers, setDrivers] = useState([]);
  const [showDriverDialog, setShowDriverDialog] = useState(false);
  const [driverOrderId, setDriverOrderId] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Live-Bestellungen laden (aktive)
  const fetchLiveOrders = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/delivery/orders`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        // Sortieren nach Priorität und Zeit
        const sorted = (data || []).sort((a, b) => {
          const priorityA = ORDER_STATUS[a.status]?.priority || 50;
          const priorityB = ORDER_STATUS[b.status]?.priority || 50;
          if (priorityA !== priorityB) return priorityA - priorityB;
          return new Date(a.created_at) - new Date(b.created_at);
        });
        setLiveOrders(sorted);
      }
    } catch (error) {
      console.error('Error fetching live orders:', error);
    }
  }, []);

  // Alle Bestellungen laden
  const fetchOrders = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    else setRefreshing(true);
    
    try {
      const params = new URLSearchParams();
      params.append('date', dateFilter === 'custom' ? customDate : dateFilter);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await fetch(`${API_BASE}/api/orders/overview?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateFilter, customDate, typeFilter, statusFilter]);

  // Fahrer laden
  const fetchDrivers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/delivery/drivers`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDrivers(data.drivers || []);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  }, []);

  // Produkte laden
  const fetchProducts = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/products-v2/`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || data || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, []);

  useEffect(() => {
    fetchLiveOrders();
    fetchOrders();
    fetchDrivers();
    fetchProducts();
    
    // Auto-Refresh für Live-Tab
    const interval = setInterval(() => {
      if (activeTab === 'live') {
        fetchLiveOrders();
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [fetchLiveOrders, fetchOrders, fetchDrivers, fetchProducts, activeTab]);

  // Tab wechsel → Daten laden
  useEffect(() => {
    if (activeTab === 'live') {
      fetchLiveOrders();
    } else {
      fetchOrders();
    }
  }, [activeTab, fetchLiveOrders, fetchOrders]);

  // Status ändern (Live-Bestellung)
  const updateLiveStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/delivery/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus, actor: 'kasse' })
      });
      
      if (response.ok) {
        toast.success(`Status: ${ORDER_STATUS[newStatus]?.label}`);
        fetchLiveOrders();
      } else {
        toast.error('Fehler beim Statuswechsel');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Fahrer zuweisen
  const assignDriver = async () => {
    if (!driverOrderId || !selectedDriverId) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/delivery/orders/${driverOrderId}/assign-driver`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ driver_id: selectedDriverId, actor: 'kasse' })
      });
      
      if (response.ok) {
        toast.success('Fahrer zugewiesen');
        setShowDriverDialog(false);
        setDriverOrderId(null);
        setSelectedDriverId('');
        fetchLiveOrders();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Zuweisung fehlgeschlagen');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Bestellung öffnen
  const openOrderDetail = async (order) => {
    setSelectedOrder(order);
    setDetailOpen(true);
    setEditMode(false);
  };

  // Bearbeitung starten
  const startEditing = () => {
    setEditedOrder(JSON.parse(JSON.stringify(selectedOrder)));
    setEditMode(true);
  };

  // Bearbeitung abbrechen
  const cancelEditing = () => {
    setEditedOrder(null);
    setEditMode(false);
  };

  // Zahlungsart ändern
  const changePaymentMethod = (newMethod) => {
    setEditedOrder(prev => ({ ...prev, payment_method: newMethod }));
  };

  // Status ändern
  const changeStatus = (newStatus) => {
    setEditedOrder(prev => ({ ...prev, status: newStatus }));
  };

  // Artikel Menge ändern
  const changeItemQuantity = (itemIndex, delta) => {
    setEditedOrder(prev => {
      const newItems = [...(prev.items || [])];
      if (newItems[itemIndex]) {
        const newQty = Math.max(0, (newItems[itemIndex].quantity || 1) + delta);
        if (newQty === 0) {
          newItems.splice(itemIndex, 1);
        } else {
          newItems[itemIndex] = { ...newItems[itemIndex], quantity: newQty };
        }
      }
      const newTotal = newItems.reduce((sum, item) => {
        const price = item.price || item.total_gross || item.base_price || 0;
        const qty = item.quantity || 1;
        return sum + (price * qty);
      }, 0);
      return { ...prev, items: newItems, total: newTotal };
    });
  };

  // Artikel entfernen
  const removeItem = (itemIndex) => {
    setEditedOrder(prev => {
      const newItems = [...(prev.items || [])];
      newItems.splice(itemIndex, 1);
      const newTotal = newItems.reduce((sum, item) => {
        const price = item.price || item.total_gross || item.base_price || 0;
        const qty = item.quantity || 1;
        return sum + (price * qty);
      }, 0);
      return { ...prev, items: newItems, total: newTotal };
    });
  };

  // Artikel hinzufügen
  const addItem = (product) => {
    setEditedOrder(prev => {
      const newItem = {
        product_id: product.product_id || product.id,
        name: product.name,
        quantity: 1,
        price: product.base_price || product.price || 0,
        total_gross: product.base_price || product.price || 0
      };
      const newItems = [...(prev.items || []), newItem];
      const newTotal = newItems.reduce((sum, item) => {
        const price = item.price || item.total_gross || item.base_price || 0;
        const qty = item.quantity || 1;
        return sum + (price * qty);
      }, 0);
      return { ...prev, items: newItems, total: newTotal };
    });
    setShowProductPicker(false);
    setProductSearch('');
    toast.success(`${product.name} hinzugefügt`);
  };

  // Bestellung speichern
  const saveOrder = async () => {
    if (!editedOrder) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/orders/${editedOrder.order_id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          payment_method: editedOrder.payment_method,
          status: editedOrder.status,
          items: editedOrder.items,
          total: editedOrder.total,
          notes: editedOrder.notes
        })
      });
      
      if (response.ok) {
        toast.success('Bestellung aktualisiert');
        setSelectedOrder(editedOrder);
        setEditMode(false);
        setEditedOrder(null);
        fetchOrders(false);
        fetchLiveOrders();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Sortierung
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Gefilterte Bestellungen
  const filteredOrders = useMemo(() => {
    let result = [...orders];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(o => 
        o.order_number?.toString().includes(term) ||
        o.customer?.name?.toLowerCase().includes(term) ||
        o.customer?.phone?.includes(term) ||
        o.customer?.address?.toLowerCase().includes(term)
      );
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'total') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [orders, searchTerm, sortField, sortDirection]);

  // Live-Statistiken
  const liveStats = useMemo(() => {
    const active = liveOrders.filter(o => !['completed', 'delivered', 'cancelled'].includes(o.status));
    return {
      total: active.length,
      new: active.filter(o => o.status === 'new').length,
      preparing: active.filter(o => o.status === 'preparing').length,
      ready: active.filter(o => o.status === 'ready').length,
      onDelivery: active.filter(o => ['on_delivery', 'dispatched'].includes(o.status)).length
    };
  }, [liveOrders]);

  // Statistiken für Übersicht
  const stats = useMemo(() => {
    const total = filteredOrders.length;
    const completed = filteredOrders.filter(o => ['completed', 'delivered'].includes(o.status)).length;
    const cancelled = filteredOrders.filter(o => o.status === 'cancelled').length;
    const revenue = filteredOrders.filter(o => o.status !== 'cancelled').reduce((sum, o) => sum + (o.total || 0), 0);
    return { total, completed, cancelled, revenue };
  }, [filteredOrders]);

  // Zeit formatieren
  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  // Wartezeit berechnen
  const getWaitTime = (createdAt) => {
    if (!createdAt) return '';
    const mins = Math.floor((new Date() - new Date(createdAt)) / 60000);
    if (mins < 60) return `${mins} Min`;
    return `${Math.floor(mins/60)}h ${mins%60}m`;
  };

  // Gefilterte Produkte
  const filteredProducts = useMemo(() => {
    if (!productSearch) return products.slice(0, 20);
    const term = productSearch.toLowerCase();
    return products.filter(p => p.name?.toLowerCase().includes(term)).slice(0, 20);
  }, [products, productSearch]);

  // Export
  const exportOrders = () => {
    const csv = [
      ['Nr.', 'Datum', 'Zeit', 'Typ', 'Status', 'Kunde', 'Betrag', 'Portal'].join(';'),
      ...filteredOrders.map(o => [
        o.order_number, formatDate(o.created_at), formatTime(o.created_at),
        ORDER_TYPES[o.type]?.label || o.type, ORDER_STATUS[o.status]?.label || o.status,
        o.customer?.name || '', o.total?.toFixed(2) || '', o.portal || ''
      ].join(';'))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bestellungen_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    toast.success('Export erstellt');
  };

  // Sort Icon
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-purple-400" />
      : <ArrowDown className="w-3 h-3 ml-1 text-purple-400" />;
  };

  // Aktuelles Order-Objekt für Dialog
  const displayOrder = editMode ? editedOrder : selectedOrder;

  // Nächste Aktionen für Live-Bestellung
  const getNextActions = (order) => {
    const actions = [];
    if (order.status === 'new') {
      actions.push({ status: 'preparing', label: 'Zubereitung starten', icon: ChefHat, color: 'bg-purple-600' });
    }
    if (order.status === 'preparing') {
      actions.push({ status: 'ready', label: 'Fertig', icon: CheckCircle, color: 'bg-emerald-600' });
    }
    if (order.status === 'ready' && order.type === 'delivery') {
      actions.push({ status: 'dispatched', label: 'Unterwegs', icon: Car, color: 'bg-orange-600' });
    }
    if (order.status === 'ready' && order.type !== 'delivery') {
      actions.push({ status: 'delivered', label: 'Abgeholt', icon: CheckCircle, color: 'bg-green-600' });
    }
    if (['dispatched', 'on_delivery'].includes(order.status)) {
      actions.push({ status: 'delivered', label: 'Zugestellt', icon: Home, color: 'bg-green-600' });
    }
    return actions;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin mx-auto mb-4 text-purple-400" />
          <p className="text-gray-400">Bestellungen werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6" data-testid="bestelluebersicht-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Bestellungen</h1>
          <p className="text-gray-400 text-sm">Live-Übersicht & Bestellhistorie</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => { fetchLiveOrders(); fetchOrders(false); }}
            disabled={refreshing}
            className="border-slate-600"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
          {activeTab === 'history' && (
            <Button variant="outline" onClick={exportOrders} className="border-slate-600">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-800 border border-slate-700 p-1">
          <TabsTrigger value="live" className="data-[state=active]:bg-purple-600 gap-2">
            <Zap className="w-4 h-4" />
            Live
            {liveStats.total > 0 && (
              <Badge className="bg-yellow-500 text-black ml-1">{liveStats.total}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-purple-600 gap-2">
            <Calendar className="w-4 h-4" />
            Übersicht
          </TabsTrigger>
        </TabsList>

        {/* ===================== LIVE TAB ===================== */}
        <TabsContent value="live" className="space-y-4">
          {/* Live Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className={`border-2 ${liveStats.new > 0 ? 'bg-yellow-500/20 border-yellow-500' : 'bg-slate-800/50 border-slate-700'}`}>
              <CardContent className="py-3 px-4 text-center">
                <p className={`text-3xl font-bold ${liveStats.new > 0 ? 'text-yellow-400 animate-pulse' : 'text-white'}`}>{liveStats.new}</p>
                <p className="text-xs text-gray-400">Neu</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-500/10 border-purple-500/30">
              <CardContent className="py-3 px-4 text-center">
                <p className="text-3xl font-bold text-purple-400">{liveStats.preparing}</p>
                <p className="text-xs text-gray-400">Zubereitung</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-500/10 border-emerald-500/30">
              <CardContent className="py-3 px-4 text-center">
                <p className="text-3xl font-bold text-emerald-400">{liveStats.ready}</p>
                <p className="text-xs text-gray-400">Fertig</p>
              </CardContent>
            </Card>
            <Card className="bg-orange-500/10 border-orange-500/30">
              <CardContent className="py-3 px-4 text-center">
                <p className="text-3xl font-bold text-orange-400">{liveStats.onDelivery}</p>
                <p className="text-xs text-gray-400">Unterwegs</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-3 px-4 text-center">
                <p className="text-3xl font-bold text-white">{liveStats.total}</p>
                <p className="text-xs text-gray-400">Aktiv</p>
              </CardContent>
            </Card>
          </div>

          {/* Live Orders Grid */}
          {liveOrders.filter(o => !['completed', 'delivered', 'cancelled'].includes(o.status)).length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-16 text-center">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400 opacity-50" />
                <h3 className="text-xl font-medium text-white mb-2">Keine aktiven Bestellungen</h3>
                <p className="text-gray-400">Neue Bestellungen erscheinen hier automatisch</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {liveOrders.filter(o => !['completed', 'delivered', 'cancelled'].includes(o.status)).map((order) => {
                const StatusIcon = ORDER_STATUS[order.status]?.icon || AlertCircle;
                const TypeIcon = ORDER_TYPES[order.type]?.icon || ShoppingBag;
                const nextActions = getNextActions(order);
                const waitTime = getWaitTime(order.created_at);
                const isUrgent = parseInt(waitTime) > 30;
                
                return (
                  <Card 
                    key={order.order_id || order.id}
                    className={`border-2 transition-all hover:scale-[1.02] cursor-pointer ${
                      order.status === 'new' ? 'bg-yellow-500/10 border-yellow-500/50' :
                      order.status === 'preparing' ? 'bg-purple-500/10 border-purple-500/30' :
                      order.status === 'ready' ? 'bg-emerald-500/10 border-emerald-500/50 animate-pulse' :
                      'bg-slate-800/50 border-slate-700'
                    }`}
                    onClick={() => openOrderDetail(order)}
                  >
                    <CardContent className="p-4">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-white">#{order.order_number || order.id?.slice(-4)}</span>
                          <Badge className={ORDER_STATUS[order.status]?.color + ' text-white'}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {ORDER_STATUS[order.status]?.label}
                          </Badge>
                        </div>
                        <div className={`flex items-center gap-1 text-sm ${isUrgent ? 'text-red-400' : 'text-gray-400'}`}>
                          <Timer className="w-4 h-4" />
                          {waitTime}
                        </div>
                      </div>
                      
                      {/* Typ & Portal */}
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline" className="border-slate-600">
                          <TypeIcon className="w-3 h-3 mr-1" />
                          {ORDER_TYPES[order.type]?.label || order.type}
                        </Badge>
                        {order.portal && order.portal !== 'own' && order.portal !== 'pos' && (
                          <Badge variant="outline" className={PORTALS[order.portal]?.color || 'border-slate-600'}>
                            {PORTALS[order.portal]?.label || order.portal}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Kunde */}
                      <div className="mb-3">
                        <p className="text-white font-medium">{order.customer?.name || order.customer_name || 'Kunde'}</p>
                        {order.customer?.address && (
                          <p className="text-gray-400 text-sm truncate">{order.customer.address}</p>
                        )}
                      </div>
                      
                      {/* Betrag */}
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-400 text-sm">{order.items?.length || '?'} Artikel</span>
                        <span className="text-xl font-bold text-white">{(order.total || order.total_gross || 0).toFixed(2)}€</span>
                      </div>
                      
                      {/* Fahrer */}
                      {order.type === 'delivery' && (
                        <div className="mb-3">
                          {order.driver_name || order.driver ? (
                            <div className="flex items-center gap-2 text-sm">
                              <UserCheck className="w-4 h-4 text-green-400" />
                              <span className="text-green-400">{order.driver_name || order.driver}</span>
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="w-full border-amber-500 text-amber-400"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDriverOrderId(order.order_id || order.id);
                                setShowDriverDialog(true);
                              }}
                            >
                              <UserCheck className="w-4 h-4 mr-2" />
                              Fahrer zuweisen
                            </Button>
                          )}
                        </div>
                      )}
                      
                      {/* Actions */}
                      {nextActions.length > 0 && (
                        <div className="flex gap-2 mt-2">
                          {nextActions.map((action, idx) => (
                            <Button
                              key={idx}
                              size="sm"
                              className={`flex-1 ${action.color} hover:opacity-90`}
                              onClick={(e) => {
                                e.stopPropagation();
                                updateLiveStatus(order.order_id || order.id, action.status);
                              }}
                            >
                              <action.icon className="w-4 h-4 mr-1" />
                              {action.label}
                            </Button>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ===================== HISTORY TAB ===================== */}
        <TabsContent value="history" className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-3 px-4">
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-gray-400">Gesamt</p>
              </CardContent>
            </Card>
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="py-3 px-4">
                <p className="text-2xl font-bold text-green-400">{stats.completed}</p>
                <p className="text-xs text-gray-400">Abgeschlossen</p>
              </CardContent>
            </Card>
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="py-3 px-4">
                <p className="text-2xl font-bold text-red-400">{stats.cancelled}</p>
                <p className="text-xs text-gray-400">Storniert</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-500/10 border-purple-500/30">
              <CardContent className="py-3 px-4">
                <p className="text-2xl font-bold text-purple-400">{stats.revenue.toFixed(2)}€</p>
                <p className="text-xs text-gray-400">Umsatz</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px] max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 bg-slate-900 border-slate-600"
                  />
                </div>
                
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-[130px] bg-slate-900 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Heute</SelectItem>
                    <SelectItem value="yesterday">Gestern</SelectItem>
                    <SelectItem value="week">7 Tage</SelectItem>
                    <SelectItem value="month">30 Tage</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[120px] bg-slate-900 border-slate-600">
                    <SelectValue placeholder="Typ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Typen</SelectItem>
                    <SelectItem value="delivery">Lieferung</SelectItem>
                    <SelectItem value="pickup">Abholung</SelectItem>
                    <SelectItem value="table">Tisch</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[130px] bg-slate-900 border-slate-600">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="completed">Abgeschlossen</SelectItem>
                    <SelectItem value="cancelled">Storniert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Orders Table */}
          <Card className="bg-slate-800/50 border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/50">
                  <tr className="border-b border-slate-700">
                    <th className="py-3 px-3 text-left">
                      <button className="flex items-center text-gray-400 hover:text-white font-medium text-xs uppercase" onClick={() => handleSort('status')}>
                        Status <SortIcon field="status" />
                      </button>
                    </th>
                    <th className="py-3 px-3 text-left">
                      <button className="flex items-center text-gray-400 hover:text-white font-medium text-xs uppercase" onClick={() => handleSort('order_number')}>
                        Nr. <SortIcon field="order_number" />
                      </button>
                    </th>
                    <th className="py-3 px-3 text-left">
                      <button className="flex items-center text-gray-400 hover:text-white font-medium text-xs uppercase" onClick={() => handleSort('created_at')}>
                        Zeit <SortIcon field="created_at" />
                      </button>
                    </th>
                    <th className="py-3 px-3 text-left text-gray-400 font-medium text-xs uppercase">Kunde</th>
                    <th className="py-3 px-3 text-left text-gray-400 font-medium text-xs uppercase">Zahlung</th>
                    <th className="py-3 px-3 text-right">
                      <button className="flex items-center justify-end w-full text-gray-400 hover:text-white font-medium text-xs uppercase" onClick={() => handleSort('total')}>
                        Betrag <SortIcon field="total" />
                      </button>
                    </th>
                    <th className="py-3 px-3 text-left text-gray-400 font-medium text-xs uppercase">Typ</th>
                    <th className="py-3 px-3 text-left text-gray-400 font-medium text-xs uppercase">Portal</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr><td colSpan={8} className="py-12 text-center text-gray-400">Keine Bestellungen</td></tr>
                  ) : (
                    filteredOrders.map((order, idx) => {
                      const TypeIcon = ORDER_TYPES[order.type]?.icon || ShoppingBag;
                      const PaymentIcon = PAYMENT_METHODS[order.payment_method]?.icon || CreditCard;
                      const isCancelled = order.status === 'cancelled';
                      
                      return (
                        <tr 
                          key={order.order_id || idx}
                          className="border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer"
                          onClick={() => openOrderDetail(order)}
                        >
                          <td className="py-3 px-3">
                            <Badge className={`${ORDER_STATUS[order.status]?.color} text-white text-xs`}>
                              {ORDER_STATUS[order.status]?.label || order.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-3">
                            <span className="font-mono font-bold text-white">#{order.order_number}</span>
                          </td>
                          <td className="py-3 px-3 text-gray-300">
                            <div>{formatTime(order.created_at)}</div>
                            <div className="text-xs text-gray-500">{formatDate(order.created_at)}</div>
                          </td>
                          <td className="py-3 px-3">
                            <span className="text-white">{order.customer?.name || 'ANONYM'}</span>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-1">
                              <PaymentIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-300 text-xs">{PAYMENT_METHODS[order.payment_method]?.label || order.payment_method}</span>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-right">
                            <span className={`font-bold ${isCancelled ? 'text-gray-500 line-through' : 'text-white'}`}>
                              {order.total?.toFixed(2)}€
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-1">
                              <TypeIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-xs text-gray-400">{ORDER_TYPES[order.type]?.label || order.type}</span>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            {order.portal ? (
                              <Badge variant="outline" className={`text-xs ${PORTALS[order.portal]?.color || ''}`}>
                                {PORTALS[order.portal]?.label || order.portal}
                              </Badge>
                            ) : <span className="text-gray-600">-</span>}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Fahrer zuweisen Dialog */}
      <Dialog open={showDriverDialog} onOpenChange={setShowDriverDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Fahrer zuweisen</DialogTitle>
          </DialogHeader>
          <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
            <SelectTrigger className="bg-slate-900 border-slate-600">
              <SelectValue placeholder="Fahrer wählen..." />
            </SelectTrigger>
            <SelectContent>
              {drivers.map((driver) => (
                <SelectItem key={driver.driver_id || driver.id} value={driver.driver_id || driver.id}>
                  {driver.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDriverDialog(false)}>Abbrechen</Button>
            <Button onClick={assignDriver} disabled={!selectedDriverId} className="bg-purple-600">Zuweisen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bestellungs-Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={(open) => { setDetailOpen(open); if (!open) cancelEditing(); }}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Receipt className="w-5 h-5 text-purple-400" />
                <span>Bestellung #{displayOrder?.order_number}</span>
                <Badge className={`${ORDER_STATUS[displayOrder?.status]?.color} text-white`}>
                  {ORDER_STATUS[displayOrder?.status]?.label}
                </Badge>
              </div>
              {!editMode ? (
                <Button onClick={startEditing} variant="outline" size="sm" className="border-purple-500 text-purple-400">
                  <Edit className="w-4 h-4 mr-2" />Bearbeiten
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={cancelEditing} variant="outline" size="sm">Abbrechen</Button>
                  <Button onClick={saveOrder} disabled={saving} className="bg-green-600">
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Speichern
                  </Button>
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {displayOrder && (
            <Tabs defaultValue="details" className="mt-4">
              <TabsList className="bg-slate-900">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="items">Artikel ({(displayOrder.items || []).length})</TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-slate-900/50 border-slate-700">
                    <CardContent className="pt-4">
                      <Label className="text-gray-400 text-xs">Kunde</Label>
                      <p className="text-white font-medium">{displayOrder.customer?.name || 'ANONYM'}</p>
                      {displayOrder.customer?.phone && <p className="text-gray-400 text-sm">{displayOrder.customer.phone}</p>}
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-900/50 border-slate-700">
                    <CardContent className="pt-4">
                      <Label className="text-gray-400 text-xs">Adresse</Label>
                      <p className="text-white">{displayOrder.customer?.address || '-'}</p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400 text-xs mb-2 block">Status</Label>
                    {editMode ? (
                      <Select value={editedOrder.status} onValueChange={changeStatus}>
                        <SelectTrigger className="bg-slate-900 border-slate-600"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(ORDER_STATUS).map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`${ORDER_STATUS[displayOrder.status]?.color} text-white`}>
                        {ORDER_STATUS[displayOrder.status]?.label}
                      </Badge>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs mb-2 block">Zahlungsart</Label>
                    {editMode ? (
                      <Select value={editedOrder.payment_method} onValueChange={changePaymentMethod}>
                        <SelectTrigger className="bg-slate-900 border-slate-600"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(PAYMENT_METHODS).map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-white">{PAYMENT_METHODS[displayOrder.payment_method]?.label || displayOrder.payment_method}</span>
                    )}
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="items" className="mt-4">
                <div className="space-y-2">
                  {(displayOrder.items || []).map((item, idx) => (
                    <Card key={idx} className="bg-slate-900/50 border-slate-700">
                      <CardContent className="py-3 px-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Package className="w-4 h-4 text-purple-400" />
                          <span className="text-white">{item.name || item.product_name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          {editMode ? (
                            <>
                              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeItemQuantity(idx, -1)}><Minus className="w-3 h-3" /></Button>
                              <span className="text-white w-6 text-center">{item.quantity || 1}</span>
                              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeItemQuantity(idx, 1)}><Plus className="w-3 h-3" /></Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => removeItem(idx)}><Trash2 className="w-3 h-3" /></Button>
                            </>
                          ) : (
                            <span className="text-gray-400">{item.quantity || 1}x</span>
                          )}
                          <span className="text-white font-bold min-w-[60px] text-right">
                            {((item.price || item.total_gross || 0) * (item.quantity || 1)).toFixed(2)}€
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {editMode && (
                    showProductPicker ? (
                      <Card className="bg-slate-900 border-slate-600">
                        <CardContent className="py-3">
                          <Input placeholder="Produkt suchen..." value={productSearch} onChange={(e) => setProductSearch(e.target.value)} className="mb-2" autoFocus />
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            {filteredProducts.map((p) => (
                              <button key={p.product_id || p.id} className="w-full text-left px-3 py-2 rounded hover:bg-slate-700 flex justify-between" onClick={() => addItem(p)}>
                                <span className="text-white">{p.name}</span>
                                <span className="text-gray-400">{(p.base_price || 0).toFixed(2)}€</span>
                              </button>
                            ))}
                          </div>
                          <Button variant="ghost" className="w-full mt-2" onClick={() => setShowProductPicker(false)}>Abbrechen</Button>
                        </CardContent>
                      </Card>
                    ) : (
                      <Button variant="outline" className="w-full border-dashed" onClick={() => setShowProductPicker(true)}>
                        <Plus className="w-4 h-4 mr-2" />Artikel hinzufügen
                      </Button>
                    )
                  )}
                </div>
                
                <Separator className="bg-slate-700 my-4" />
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Gesamt</span>
                  <span className="text-2xl font-bold text-white">{(displayOrder.total || 0).toFixed(2)}€</span>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
